import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import ConfirmModal from '../../components/common/ConfirmModal';
import {
  FolderOpen,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader,
  CheckCircle,
  AlertCircle,
  ChevronRight
} from 'lucide-react';

function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, project: null });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await adminAPI.getProjects();
      setProjects(res.data.projects || []);
    } catch (error) {
      showToast('Error al cargar proyectos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 1500);
  };

  const openCreateModal = () => {
    setEditingProject(null);
    setFormData({ name: '', description: '' });
    setModalOpen(true);
  };

  const openEditModal = (p) => {
    setEditingProject(p);
    setFormData({ name: p.name, description: p.description || '' });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingProject) {
        await adminAPI.updateProject(editingProject.id, formData);
        showToast('Proyecto actualizado correctamente');
      } else {
        await adminAPI.createProject(formData);
        showToast('Proyecto creado correctamente');
      }
      setModalOpen(false);
      fetchProjects();
    } catch (error) {
      showToast(error.response?.data?.error || 'Error al guardar proyecto', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (p) => {
    setDeleteModal({ open: true, project: p });
  };

  const confirmDelete = async () => {
    const p = deleteModal.project;
    setDeleteModal({ open: false, project: null });

    try {
      await adminAPI.deleteProject(p.id);
      showToast('Proyecto eliminado correctamente');
      fetchProjects();
    } catch (error) {
      showToast(error.response?.data?.error || 'Error al eliminar proyecto', 'error');
    }
  };

  return (
    <AdminLayout title={<h1>Gestión de Proyectos</h1>}>
      {loading ? (
        <div className="admin-loading">
          <Loader size={32} className="loading-spinner" />
        </div>
      ) : projects.length === 0 ? (
        <div className="empty-section centered">
          <FolderOpen size={64} />
          <h2>No hay proyectos</h2>
          <p>Crea tu primer proyecto para comenzar</p>
          <button className="btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Crear Proyecto
          </button>
        </div>
      ) : (
        <>
          <div className="content-header">
            <button className="btn-primary" onClick={openCreateModal}>
              <Plus size={20} />
              Nuevo Proyecto
            </button>
          </div>
          <div className="projects-grid">
            {projects.map(p => (
            <div key={p.id} className="project-card">
              <div className="project-card-header">
                <FolderOpen size={24} />
                <h3>{p.name}</h3>
                <div className="project-card-actions">
                  <button className="btn-icon" onClick={() => openEditModal(p)} title="Editar">
                    <Edit2 size={16} />
                  </button>
                  <button className="btn-icon danger" onClick={() => handleDelete(p)} title="Eliminar">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <p className="project-description">{p.description || 'Sin descripción'}</p>
              <div className="project-stats">
                <span>{p.stats?.areasCount || 0} áreas</span>
                <span>{p.stats?.documentsCount || 0} documentos</span>
                <span>{p.stats?.usersCount || 0} usuarios</span>
              </div>
              <Link to={`/admin/projects/${p.id}`} className="project-link">
                Ver áreas y documentos <ChevronRight size={16} />
              </Link>
            </div>
          ))}
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'}</h2>
              <button className="modal-close" onClick={() => setModalOpen(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Cliente ABC"
                  required
                />
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del proyecto..."
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? <Loader size={16} className="loading-spinner" /> : null}
                  {editingProject ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.open}
        title="Eliminar Proyecto"
        message={`¿Estás seguro de eliminar el proyecto "${deleteModal.project?.name}"? Se eliminarán todas sus áreas y documentos. Esta acción no se puede deshacer.`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModal({ open: false, project: null })}
      />

      {/* Toast */}
      {toast && (
        <div className={`toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {toast.message}
        </div>
      )}
    </AdminLayout>
  );
}

export default ProjectsPage;
